// Admin auth context. Mirrors the user-side auth.tsx but probes
// /v1/admin/whoami and exposes a logout that posts to
// /v1/admin/logout. Independent from the user AuthProvider — an admin
// can be logged in here without affecting their user session.
//
// The plumbing (probe + status + logout) is shared with the user side
// via lib/useSession (ADR 0042 task p_e2e_boat_specs). What stays here is the
// admin-specific record shape and context wiring.

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { useSession } from '../lib/useSession';

interface AdminWhoami {
  userId: string;
  email: string;
  sessionExpiresAt: string | null;
  sessionTtlSeconds: number;
}

type Status = 'unknown' | 'authenticated' | 'anonymous';

interface AdminAuthValue {
  status: Status;
  admin: AdminWhoami | null;
  refresh: () => Promise<Status>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const session = useSession<AdminWhoami>({
    whoamiUrl: '/v1/admin/whoami',
    logoutUrl: '/v1/admin/logout',
    parseRecord: (raw) => raw as AdminWhoami,
  });

  const value = useMemo<AdminAuthValue>(
    () => ({
      status: session.status,
      admin: session.record,
      refresh: session.refresh,
      logout: session.logout,
    }),
    [session.status, session.record, session.refresh, session.logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext);
  if (ctx === null) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  return ctx;
}
